"use no memo";

import { type ReactNode } from "react";

import { Heading } from "@stacklet/ui";
import { FormProvider, type UseFormReturn } from "react-hook-form";

import { HelpDrawer } from "../HelpDrawer/HelpDrawer";
import { type GroupFormSchema } from "./GroupSchema";

interface Props {
  /** The form title to display */
  title: string;
  /** Whether the help panel is open */
  isHelpOpen: boolean;
  /** Callback when help panel open state changes */
  onHelpToggle: (open: boolean) => void;
  /** Form instance from react-hook-form */
  form: UseFormReturn<GroupFormSchema>;
  /** Form submit handler */
  onSubmit: () => void;
  /** Form content to render (should include action buttons inline) */
  children: ReactNode;
}

export function GroupFormLayout({
  title,
  isHelpOpen,
  onHelpToggle,
  form,
  onSubmit,
  children,
}: Props) {
  return (
    <div className="flex min-h-screen w-full gap-x-lg">
      {/* Main content column - 2/3 width when help is open */}
      <div className="flex-[2]">
        <div className="mb-md flex min-h-[3rem] items-start justify-between">
          <Heading level={2}>{title}</Heading>
          <HelpDrawer isOpen={isHelpOpen} onToggle={onHelpToggle} buttonOnly />
        </div>

        <form
          className="flex min-h-[calc(100vh-200px)] flex-col"
          onSubmit={onSubmit}
        >
          <FormProvider {...form}>
            <div className="flex-1">{children}</div>
          </FormProvider>
        </form>
      </div>

      {/* Help drawer column - 1/3 width */}
      {isHelpOpen ? (
        <div className="relative flex-1">
          <HelpDrawer isOpen={isHelpOpen} onToggle={onHelpToggle} />
        </div>
      ) : null}
    </div>
  );
}
